import { Box, IconButton, Typography } from '@mui/material';
import { MdOutlineEdit } from 'react-icons/md';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { GoPlus } from 'react-icons/go';
import { useDeleteBusiness, useGetBusinesses } from 'apis/agency/hook';
import { BusinessProfileResponse } from 'apis/business/types';
import Modal from 'components/Common/Modal';
import BusinessForm from './components/BusinessForm';
import { useState } from 'react';
import Pagination from 'components/Common/Pagination';
import { CacheKeys } from 'utils/cacheKeys';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useGetAgencyConfiguration } from 'apis/agency/configuration/hook';

export default function Businesses() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: businesses, isLoading } = useGetBusinesses({ page: currentPage });
  const { mutateAsync: deleteBusiness } = useDeleteBusiness();
  const { data: agencyConfigData } = useGetAgencyConfiguration();
  const { setSelectedBusiness, setSelectedWorkspace } = useBusiness();

  const handleEditBusiness = (business: BusinessProfileResponse) => {
    if (business) {
      setSelectedBusiness(Number(business.id));
      setSelectedWorkspace(Number(business?.workspaces?.[0].id));
      localStorage.setItem(CacheKeys.businessIds, JSON.stringify([business.id]));
      localStorage.setItem(CacheKeys.workspaceIds, JSON.stringify(business?.workspaces?.map((item) => item.id)));
      navigate(String(business.id));
    }
  };

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2} mt={3}>
        <Typography variant='h2' fontWeight={500}>
          Businesses
        </Typography>
        <Modal
          button={(openModal: any) => (
            <Button onClick={() => openModal()} variant='contained' color='primary' startIcon={<GoPlus />}>
              Add Business
            </Button>
          )}
        >
          {(handleCloseModal: any) => <BusinessForm handleClose={handleCloseModal} />}
        </Modal>
      </Box>
      <DataTable
        data={businesses?.data}
        columns={columns(deleteBusiness, handleEditBusiness, agencyConfigData?.billing_enabled)}
        isHeaderSticky={true}
        isLoading={isLoading}
      />
      <Pagination totalPages={businesses?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
    </Box>
  );
}

type DeleteBusinessFn = (id: number) => void;

const columns = (
  deleteBusiness: DeleteBusinessFn,
  handleEditBusiness: any,
  isBillingEnabled?: boolean
): ColumnDef<BusinessProfileResponse>[] => [
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Email',
    accessorKey: 'email'
  },
  {
    header: 'AGENTS',
    accessorKey: 'agents_count'
  },
  ...(isBillingEnabled
    ? [
        {
          header: 'Payment STATUS',
          cell: (info: any) => (
            <Typography
              sx={{
                color: info.row.original.payment_status === 'active_billing' ? '#00860D' : '#FB8703',
                border:
                  info.row.original.payment_status === 'active_billing' ? '1px solid #00860D' : '1px solid #FB8703',
                borderRadius: '8px',
                padding: '4px 8px',
                width: 'fit-content',
                fontSize: '12px'
              }}
            >
              {info.row.original.payment_status === 'active_billing' ? 'BILLED' : 'PENDING'}
            </Typography>
          )
        }
      ]
    : []),
  {
    header: 'Account Status',
    cell: (info) => (
      <Typography
        sx={{
          color: info.row.original.account_status ? '#00860D' : '#DA0808',
          border: info.row.original.account_status ? '1px solid #00860D' : '1px solid #DA0808',
          borderRadius: '8px',
          padding: '4px 8px',
          width: 'fit-content',
          fontSize: '12px'
        }}
      >
        {info.row.original.account_status ? 'Enabled' : 'Disabled'}
      </Typography>
    )
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <IconButton onClick={() => handleEditBusiness(info.row.original)}>
          <MdOutlineEdit />
        </IconButton>
        <IconButton onClick={() => deleteBusiness(info.row.original.id)} color='error'>
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];
